/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable max-len */
import React, { useContext, useMemo } from 'react';
import {
  bool as boolType,
  any,
  string as stringType,
  number as numType
} from 'prop-types';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import {
  bool,
  extend,
  string,
  shape,
  QueryProvider,
  QueryContext,
  params
} from '@thd-nucleus/data-sources';
import { Carousel } from '@one-thd/sui-carousel';
import { Typography } from '@one-thd/sui-atomic-components';
import classNames from 'classnames';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { LoadingPlaceholder } from '../core/LoadingPlaceholder';
import {
  getLoyaltyMembershipInput,
  getBBTTitleClassName,
  getBBTClassName,
  createBBTCategoryArrays,
  getDrecsImpressionID,
  getDrecsComponent,
  getDrecsimpressionType,
} from '../utils/helpers';
import BBTProTipContainer from './BBTProTipContainer';
import { RecsProductPod } from '../core/RecsProductPod';
import './BBTStyles.scss';

const Wrapper = ({ children }) => children;
Wrapper.dataModel = extend({
  product: params({ itemId: string().isRequired() }).shape({
    dataSource: string()
  })
}, RecsProductPod);

const BBTCarousel = (props) => {
  const { channel } = useContext(ExperienceContext);
  const { defaultVariables } = useContext(QueryContext) || {};
  const isMobile = channel === 'mobile';
  const {
    data,
    scheme,
    hideATC,
    showLoading,
    loading,
    hideTitle,
    storeId,
    zipCode,
    membershipInformation,
    analyticsAnchorProductSku,
    parent,
    hideRating,
    noATCFulfillment,
    analyticImpressionData,
    impressionData
  } = props;

  const bbtCategoryThreshold = useConfigService('fs:bbtCategoryThreshold') || 2;

  const categoryArrays = useMemo(() => {
    if (!data?.categories || data?.categories?.length < bbtCategoryThreshold || !data?.products) return [];
    return createBBTCategoryArrays(data);
  }, [data]);

  if (showLoading && loading) {
    return <LoadingPlaceholder txtPlaceholderLines={7} hideTitle={hideTitle} />;
  }

  if (!loading && (!data?.categories || data?.categories?.length < bbtCategoryThreshold || categoryArrays.length === 0)) {
    return null;
  }

  const { metadata } = data || {};
  const classes = getBBTClassName(isMobile);
  const bbtTitleClasses = getBBTTitleClassName(isMobile);
  const qtxDefaultVariables = {
    storeId,
    zipCode,
    installationServices: false,
    loyaltyMembershipInput: getLoyaltyMembershipInput(membershipInformation),
    ...(typeof defaultVariables?.current?.isBrandPricingPolicyCompliant === 'boolean' && {
      isBrandPricingPolicyCompliant: defaultVariables.current.isBrandPricingPolicyCompliant
    })
  };

  let breakpoints = ({
    sm: {
      slidesPerView: 'auto',
      slidesPerGroup: 1
    },
    md: {
      slidesPerView: 'auto',
      slidesPerGroup: 2
    },
    lg: {
      slidesPerView: 'auto',
      slidesPerGroup: 3
    },
    xl: {
      slidesPerView: 'auto',
      slidesPerGroup: 3
    },
    '2xl': {
      slidesPerView: 'auto',
      slidesPerGroup: 3
    }
  });

  const carouselProps = isMobile ? {
    slidesPerView: 1,
    slidesPerGroup: 1,
    peek: 'sm',
    spaceBetween: 12,
  } : {
    peek: 'md',
    spaceBetween: 12,
  };

  return (
    <div className={classNames(classes, {
      'sui-pb-2': (categoryArrays.length <= 3 && !isMobile)
    })}
    >
      <div className={isMobile ? bbtTitleClasses : 'sui-left-column'}>
        <BBTProTipContainer metadata={metadata} isMobile={isMobile} />
      </div>
      <div className={isMobile ? 'sui-col-span-12 sui-bbt-mb-columns' : 'sui-col-span-5 sui-bbt-carousel-columns'}>
        <QueryProvider
          dataSource={scheme}
          cacheKey={`${scheme}-recommendations-carousel`}
          defaultVariables={qtxDefaultVariables}
        >
          <Carousel
            breakpoints={breakpoints}
            disableMargin
            layout="full-bleed"
            containerId={scheme}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...carouselProps}
          >
            {categoryArrays?.map((categoryData, index) => {
              const { products, category } = categoryData;
              const isLastSlide = index === categoryArrays.length - 1;
              return (
                <ImpressionProvider
                  data={{
                    id: getDrecsImpressionID(impressionData),
                    component: getDrecsComponent(impressionData),
                    name: data?.metadata?.title,
                    type: getDrecsimpressionType(impressionData),
                    recType: 'beyond_bought_together',
                    position: '',
                    category,
                    categoryPosition: index + 1,
                  }}
                >
                  <div
                    className={classNames(
                      'sui-bbt-carousel-slide sui-p-2',
                      { 'sui-last-slide': isLastSlide }
                    )}
                    key={index}
                  >
                    <div className="sui-bbt-slide-header sui-pl-2 sui-mb-2">
                      <Typography
                        lineClamp="1"
                        variant="h4"
                        title={category}
                      >
                        {category}
                      </Typography>
                    </div>
                    <div className="sui-grid sui-grid-cols-2 sui-grid-rows-2 sui-gap-2 sui-max-w-4xl sui-mx-auto sui-multi-grid-wrapper">
                      {Array.from({ length: 4 }).map((item, slotIndex) => {
                        const productItem = products[slotIndex];
                        if (productItem) {
                          const { product } = productItem;
                          const { itemId } = product;
                          const { preferredPriceFlag } = product?.pricing || {};
                          const { canonicalUrl } = product?.identifiers || {};
                          return (
                            <div
                              key={itemId}
                              className="sui-bbt-multi-grid sui-p-2 sui-bg-white"
                            >
                              <Wrapper key={slotIndex}>
                                <RecsProductPod
                                  itemId={itemId}
                                  position={slotIndex}
                                  storeId={storeId}
                                  scheme={scheme}
                                  anchorProduct={analyticsAnchorProductSku}
                                  parent={parent}
                                  hideSwatches
                                  hideFavorites
                                  hideBadge
                                  hideSponsoredText
                                  hideATC={hideATC}
                                  hideRating={hideRating}
                                  preferredPriceFlag={preferredPriceFlag}
                                  brandTitleMaxLine={isMobile ? 3 : 2}
                                  strategy={productItem?.strategy}
                                  noATCFulfillment={noATCFulfillment}
                                  analyticImpressionData={analyticImpressionData}
                                  url={canonicalUrl}
                                />
                              </Wrapper>
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  </div>
                </ImpressionProvider>
              );
            })}
          </Carousel>
        </QueryProvider>
      </div>
    </div>
  );
};

const productModel = extend({}, {
  itemId: string(),
  dataSource: string()
}, RecsProductPod.dataModel.product);

const wrappedProduct = shape({
  category: string(),
  description: string(),
  idEndDate: string(),
  idPromoDetails: string(),
  idStartDate: string(),
  strategy: string(),
  isAnchor: bool(),
  product: params({
    dataSource: string()
  })
    .shape(productModel),
  slot: shape({
    description: string(),
    isRequired: bool(),
    name: string()
  })
});

BBTCarousel.dataModel = extend(Wrapper, wrappedProduct);
BBTCarousel.displayName = 'BBTCarousel';
BBTCarousel.propTypes = {
  ...RecsProductPod.propTypes,
  // eslint-disable-next-line react/forbid-prop-types
  data: any,
  loading: boolType,
  hideTitle: boolType,
  scheme: stringType,
  showLoading: boolType,
  storeId: stringType.isRequired,
  subtitle: stringType,
  hideATC: boolType,
  analyticsAnchorProductSku: stringType,
  hideRating: boolType,
  noATCFulfillment: boolType
};

BBTCarousel.defaultProps = {
  hideTitle: false,
  scheme: '',
  showLoading: true,
  loading: false,
  data: null,
  subtitle: null,
  hideATC: false,
  analyticsAnchorProductSku: '',
  hideRating: false,
  noATCFulfillment: true
};

export { BBTCarousel };